/**
 * "Xm ago" for the connectors page's last-sync lines.
 *
 * Shared by the page and the Investor Portals row so both render one
 * vocabulary; a row that said "2 hours ago" beside one that said "2h ago"
 * would read as two systems.
 */
import type { Temporal } from "@/utils/temporal";

export const formatRelativeTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

/** A wire temporal in the same vocabulary: "2h ago" for an instant, the
 *  calendar day itself for a date (a day has no "ago"), exhaustive by the
 *  dates-and-times rule. The one place the switch lives for the Connectors
 *  row and the Documents page's portals view alike. */
export const formatRelativeTemporal = (value: Temporal): string => {
  switch (value.granularity) {
    case "instant":
      return formatRelativeTime(value.at);
    case "date":
      return value.day;
    default: {
      const unhandled: never = value;
      throw new Error(
        `unhandled temporal granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};
